/**
 * ITER302-refine-v2 · Stronger background pattern overlays.
 *
 * Opacity + detail bumped so patterns read clearly on both light and
 * dark palettes.  New `shimmer` variant simulates the light-catch on a
 * metallic gift card.
 */
import React from 'react';

export default function VoucherBgPattern({ pattern = 'none', color = '#C9A227' }) {
  if (!pattern || pattern === 'none') return null;

  // ─── Certificate frame: double-line border with a dashed inner
  //     rule + larger Art-Deco corner brackets. ~28 % opacity so it
  //     reads clearly against navy or cream backgrounds. ────────────
  if (pattern === 'frame') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        {/* Outer solid + inner solid + dashed middle */}
        <rect x="9"  y="9"  width="182" height="262" fill="none"
              stroke={color} strokeWidth="1.5" opacity="0.85" />
        <rect x="14" y="14" width="172" height="252" fill="none"
              stroke={color} strokeWidth="0.9" opacity="0.55" />
        <rect x="11.5" y="11.5" width="177" height="257" fill="none"
              stroke={color} strokeWidth="0.5" strokeDasharray="1 2" opacity="0.6" />
        {/* Larger Art-Deco corner brackets */}
        {[
          { t: 'translate(9,9)' },
          { t: 'translate(191,9) scale(-1,1)' },
          { t: 'translate(9,271) scale(1,-1)' },
          { t: 'translate(191,271) scale(-1,-1)' },
        ].map((c, i) => (
          <g key={i} transform={c.t} stroke={color} strokeWidth="1.6"
             fill="none" opacity="0.9">
            <path d="M0 22 L0 0 L22 0" />
            <path d="M0 14 L4 14 L4 4 L14 4" strokeWidth="1.1" opacity="0.7" />
            <circle cx="9" cy="9" r="1.2" fill={color} stroke="none" />
          </g>
        ))}
      </svg>
    );
  }

  // ─── Cross-hatch: two hairline sets at opposite angles. ─────────
  if (pattern === 'diagonal_lines') {
    return (
      <svg
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        <defs>
          <pattern id="vc-diag-a" width="14" height="14"
                   patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
            <line x1="0" y1="0" x2="0" y2="14"
                  stroke={color} strokeWidth="1" opacity="0.16" />
          </pattern>
          <pattern id="vc-diag-b" width="14" height="14"
                   patternUnits="userSpaceOnUse" patternTransform="rotate(-30)">
            <line x1="0" y1="0" x2="0" y2="14"
                  stroke={color} strokeWidth="0.8" opacity="0.10" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#vc-diag-a)" />
        <rect width="100%" height="100%" fill="url(#vc-diag-b)" />
      </svg>
    );
  }

  // ─── Corner flourish: larger Art Deco ornaments in TL + BR. ─────
  if (pattern === 'corner_flourish') {
    const flourish = (
      <g stroke={color} strokeWidth="1.4" fill="none" opacity="0.9">
        {/* Outer sweeping curve */}
        <path d="M0 58 C 0 6, 6 0, 58 0" />
        {/* Inner accompanying curve */}
        <path d="M8 58 C 8 12, 12 8, 58 8" opacity="0.7" />
        {/* Small tail strokes */}
        <path d="M0 58 L0 66 M58 0 L66 0" opacity="0.6" />
        {/* Central dot and ornamental S-curve */}
        <path d="M20 30 Q30 20, 40 30 T60 30" opacity="0.6" strokeWidth="1.1" />
        <circle cx="30" cy="30" r="2.4" fill={color} stroke="none" />
        <circle cx="8"  cy="58" r="1.5" fill={color} stroke="none" />
        <circle cx="58" cy="8"  r="1.5" fill={color} stroke="none" />
      </g>
    );
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        {/* Top-left */}
        <g transform="translate(8,8)" opacity="0.28">{flourish}</g>
        {/* Bottom-right (mirrored) */}
        <g transform="translate(192,272) scale(-1,-1)" opacity="0.28">{flourish}</g>
      </svg>
    );
  }

  // ─── Shimmer: single soft diagonal light band across the card. ──
  //     CSS-only, no animation.  Adds "metallic" feel without noise.
  if (pattern === 'shimmer') {
    return (
      <div
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          background:
            `linear-gradient(115deg,`
            + ` transparent 30%,`
            + ` rgba(255,255,255,0.22) 46%,`
            + ` rgba(255,255,255,0.32) 50%,`
            + ` rgba(255,255,255,0.22) 54%,`
            + ` transparent 70%)`,
          mixBlendMode: 'screen',
        }}
      />
    );
  }

  return null;
}
